import { useMemo, useState, type ReactElement } from "react";
import { Check, Copy } from "lucide-react";
import { IconButton, Stack, Surface, Text } from "@ux/primitives";
import { palette, space } from "@ux/tokens";
import { copyToClipboard } from "../common/clipboard";
import type { SageOfficeView } from "./types";

export interface AvailableOfficesPanelProps {
    readonly offices: readonly SageOfficeView[];
    /**
     * Column name the operator needs to paste into. Both bulk-import
     * pages reference an office FK as <c>sageOfficeId</c>; promoting
     * this to a prop keeps the panel reusable for any future importer
     * that might use a different column name.
     */
    readonly columnName?: string;
}

/**
 * Reference panel showing every active sage office in the tenant with a
 * one-click copy on each id. Lives next to the bulk-import file picker
 * because that's where the operator needs ids the most — without it,
 * they had to either dig through DevTools or open each office's detail
 * panel to read its id.
 *
 * Deleted offices are filtered out: the bulk-import endpoints reject
 * them anyway, so showing them here would just be noise.
 *
 * Originally lived alongside the workspace bulk-import page; lifted
 * here when the asset bulk-import page picked up the same FK-resolution
 * requirement. Both importers now consume this single implementation.
 */
export function AvailableOfficesPanel({
    offices,
    columnName = "sageOfficeId",
}: AvailableOfficesPanelProps): ReactElement | null {
    const active = useMemo(() => offices.filter((o) => !o.isDeleted), [offices]);

    if (active.length === 0) return null;

    return (
        <Surface tone="raised" padding={4}>
            <Stack gap={3}>
                <Stack gap={1}>
                    <Text role="eyebrow">Available offices</Text>
                    <Text role="muted">
                        Use one of these ids in the <code>{columnName}</code> column. Click the copy
                        icon to grab one without leaving the page.
                    </Text>
                </Stack>
                <Stack gap={2}>
                    {active.map((o) => (
                        <OfficeReferenceRow key={o.id} office={o} />
                    ))}
                </Stack>
            </Stack>
        </Surface>
    );
}

function OfficeReferenceRow({ office }: { readonly office: SageOfficeView }): ReactElement {
    const [copied, setCopied] = useState(false);

    async function handleCopy(): Promise<void> {
        const ok = await copyToClipboard(office.id);
        if (!ok) return;
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1500);
    }

    return (
        <Stack
            direction="row"
            align="center"
            justify="space-between"
            gap={3}
            style={{
                padding: `${space[2]} ${space[3]}`,
                background: palette.surfaceInset,
                borderRadius: 6,
            }}
        >
            <Stack gap={0}>
                <Text role="body" weight="semibold">
                    {office.name}
                </Text>
                <Text role="muted">
                    <code style={{ fontSize: "0.85em" }}>{office.id}</code>
                </Text>
            </Stack>
            <IconButton
                tone="neutral"
                label={copied ? "Copied" : "Copy office id"}
                icon={copied ? <Check size={14} /> : <Copy size={14} />}
                onClick={() => {
                    void handleCopy();
                }}
            />
        </Stack>
    );
}
